import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { romanLabel, splitOptions } from "../create/paperBuilder";

// Inline markdown for question text (bold/italic/code) — p mapped to span to stay inline.
const Md = ({ text }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }) => <span>{children}</span>,
      code: ({ children }) => <code style={{ background: "#eef1f5", borderRadius: 4, padding: "0 4px", fontFamily: "monospace" }}>{children}</code>,
    }}
  >
    {text || ""}
  </ReactMarkdown>
);

// a/b may be a plain string (sample data) or { text, options, image } (built paper).
const optText = (v) => (typeof v === "string" ? v : v?.text);
const optOptions = (v) => (typeof v === "object" ? v?.options : null);
const optImage = (v) => (typeof v === "object" ? v?.image || v?.image_url : null);

const qImgStyle = { display: "block", maxWidth: "100%", maxHeight: "260px", margin: "6px 0", objectFit: "contain" };
const QImage = ({ src }) => (src ? <img src={src} alt="" className="qp-qimg" style={qImgStyle} /> : null);

const Options = ({ options }) => {
  const opts = splitOptions(options);
  if (!opts.length) return null;
  return (
    <div style={{ paddingLeft: "26px", marginTop: "2px", fontSize: "11pt" }}>
      {opts.map((o, i) => (
        <div key={i}>{o}</div>
      ))}
    </div>
  );
};

// PART B — "Either / or" questions (answer ALL).
const PartB = ({ part }) => {
  if (!part) return null;
  const marks = part.marksPerQuestion;

  // Render one option (a/b): split into i)/ii) sub-questions, or a single question.
  const renderOption = (opt, number, label) => {
    if (opt?.subs) {
      return (
        <div>
          <div className="qp-qrow"><span className="qp-qno">{number}. {label})</span></div>
          {opt.subs.map((sub, si) => (
            <div key={si}>
              <div className="qp-qrow" style={{ paddingLeft: "20px" }}>
                <span className="qp-qno">{romanLabel(si)})</span>
                <span className="qp-qtext"><Md text={sub.text} /></span>
                <span className="qp-qmarks">({sub.marks})</span>
              </div>
              <QImage src={sub.image} />
              <Options options={sub.options} />
            </div>
          ))}
        </div>
      );
    }
    return (
      <>
        <div className="qp-qrow">
          <span className="qp-qno">{number}. {label})</span>
          <span className="qp-qtext"><Md text={optText(opt)} /></span>
          {marks ? <span className="qp-qmarks">({marks})</span> : null}
        </div>
        <QImage src={optImage(opt)} />
        <Options options={optOptions(opt)} />
      </>
    );
  };

  return (
    <section className="qp-part">
      <div className="qp-part-head">
        <h3>{part.title}</h3>
        <span className="qp-marks">{part.marksLine}</span>
      </div>
      {part.instructions?.length > 0 && (
        <ul className="qp-instructions">
          {part.instructions.map((ins, idx) => (
            <li key={idx}>{ins}</li>
          ))}
        </ul>
      )}
      <div className="qp-eitheror">
        {part.questions?.map((q) => (
          <div className="qp-qpair" key={q.number}>
            {renderOption(q.a, q.number, "a")}
            <div className="qp-or">(or)</div>
            {renderOption(q.b, q.number, "b")}
          </div>
        ))}
      </div>
    </section>
  );
};

export default PartB;
